<template>
  <div class="box">
    <h3 class="title is-4">Reimbursement Data Upload</h3>
    <div class="content">
      <p>
        Upload CSV files containing reimbursement data to analyze payer performance and track
        payment patterns across different levels of care. The system processes your data with
        comprehensive validation and business rule enforcement.
      </p>

      <h4>CSV Format Requirements:</h4>
      <p class="mb-2">Your CSV file should contain the following columns in order:</p>
      <div class="table-container">
        <table class="table is-striped is-narrow">
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
              <th>Required</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>ChargeClaimID</strong></td>
              <td>Unique claim identifier</td>
              <td><span class="tag is-success">Required</span></td>
              <td>CLM123456</td>
            </tr>
            <tr>
              <td><strong>RevenueCode</strong></td>
              <td>Revenue code for the service</td>
              <td><span class="tag is-success">Required</span></td>
              <td>0126, 1002, 0912</td>
            </tr>
            <tr>
              <td><strong>ChargeBilledCPTCode</strong></td>
              <td>CPT code that was billed</td>
              <td><span class="tag is-success">Required</span></td>
              <td>90834</td>
            </tr>
            <tr>
              <td><strong>PayerName</strong></td>
              <td>Name of the insurance payer</td>
              <td><span class="tag is-info">Optional</span></td>
              <td>Blue Cross Blue Shield</td>
            </tr>
            <tr>
              <td><strong>EmployerName</strong></td>
              <td>Name of the employer</td>
              <td><span class="tag is-info">Optional</span></td>
              <td>ABC Corporation</td>
            </tr>
            <tr>
              <td><strong>GroupNumber</strong></td>
              <td>Group number</td>
              <td><span class="tag is-info">Optional</span></td>
              <td>GRP001</td>
            </tr>
            <tr>
              <td><strong>MemberID</strong></td>
              <td>Patient's member ID - required if PayerName is empty</td>
              <td><span class="tag is-info">Conditional</span></td>
              <td>ABC123456789</td>
            </tr>
            <tr>
              <td><strong>PaymentDate</strong></td>
              <td>Date of payment (MM/DD/YYYY) - defaults to current date if empty</td>
              <td><span class="tag is-info">Optional</span></td>
              <td>01/15/2024</td>
            </tr>
            <tr>
              <td><strong>PercentAllowedOnCharges</strong></td>
              <td>Percentage allowed on charges</td>
              <td><span class="tag is-success">Required</span></td>
              <td>85.50</td>
            </tr>
            <tr>
              <td><strong>ChargeDebitAmount</strong></td>
              <td>Amount charged</td>
              <td><span class="tag is-success">Required</span></td>
              <td>$2500.00</td>
            </tr>
            <tr>
              <td><strong>PaymentAllowedAmount</strong></td>
              <td>Amount allowed for payment - defaults to $0.00 if empty</td>
              <td><span class="tag is-info">Optional</span></td>
              <td>$2137.50</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4>Business Rules & Validation:</h4>

      <div class="notification is-warning is-light">
        <h5 class="has-text-weight-semibold">Revenue Code Validation</h5>
        <p>Only the following revenue codes are accepted:</p>
        <div class="tags">
          <span class="tag is-primary">0126</span>
          <span class="tag is-primary">1002</span>
          <span class="tag is-primary">0912</span>
          <span class="tag is-primary">0906</span>
          <span class="tag is-primary">0914</span>
          <span class="tag is-primary">0915</span>
        </div>
      </div>

      <div class="notification is-info is-light">
        <h5 class="has-text-weight-semibold">Minimum Charge Thresholds</h5>
        <p>Each revenue code has specific minimum charge requirements:</p>
        <div class="columns is-multiline">
          <div class="column is-half">
            <ul>
              <li>
                <strong>Rev Code 1002:</strong>
                $2,300.00 minimum
              </li>
              <li>
                <strong>Rev Code 0126:</strong>
                $2,200.00 minimum
              </li>
              <li>
                <strong>Rev Code 0912:</strong>
                $2,000.00 minimum
              </li>
            </ul>
          </div>
          <div class="column is-half">
            <ul>
              <li>
                <strong>Rev Code 0914:</strong>
                $1,050.00 minimum
              </li>
              <li>
                <strong>Rev Code 0915:</strong>
                $1,050.00 minimum
              </li>
              <li>
                <strong>Rev Code 0906:</strong>
                $1,000.00 minimum
              </li>
            </ul>
          </div>
        </div>
      </div>

      <h5 class="has-text-weight-semibold">Data Processing Rules:</h5>
      <ul>
        <li>
          <strong>Duplicate Prevention:</strong>
          Rows with existing ChargeClaimIDs will be skipped during import (not added to database)
        </li>
        <li>
          <strong>Percentage Validation:</strong>
          PercentAllowedOnCharges must be between 0-100%
        </li>
        <li>
          <strong>Date Handling:</strong>
          PaymentDate defaults to current date if empty or in invalid format
        </li>
        <li>
          <strong>Amount Handling:</strong>
          PaymentAllowedAmount defaults to $0.00 if empty
        </li>
        <li>
          <strong>Payer Handling:</strong>
          PayerName is optional - no payer association if empty
        </li>
        <li>
          <strong>MemberID Validation:</strong>
          MemberID is required only if PayerName is empty (for prefix extraction using strict rules)
        </li>
        <li>
          <strong>Row Skipping:</strong>
          Rows with both empty PayerName and MemberID will be skipped
        </li>
        <li>
          <strong>Data Normalization:</strong>
          Text fields are automatically normalized (trimmed, lowercase, special characters handled)
        </li>
        <li>
          <strong>Entity Creation:</strong>
          Payers, employers, groups, and prefixes are created automatically if they don't exist
        </li>
      </ul>

      <h5 class="has-text-weight-semibold">Alpha Prefix Extraction:</h5>
      <p>Prefixes are automatically extracted from MemberID using strict business rules:</p>
      <div class="notification is-info is-light">
        <h6 class="has-text-weight-semibold">Extraction Rules:</h6>
        <ul class="mb-2">
          <li>
            <strong>Rule 1:</strong>
            First 3 characters are letters + rest numeric → prefix = first 3 characters
          </li>
          <li>
            <strong>Rule 2:</strong>
            First 3 characters are letters + 4th or 5th is a letter → prefix = first 5 characters
          </li>
          <li>
            <strong>Rule 3:</strong>
            Any other pattern → no prefix extracted (null)
          </li>
        </ul>
        <h6 class="has-text-weight-semibold">Examples:</h6>
        <div class="columns is-multiline">
          <div class="column is-half">
            <ul class="mb-0">
              <li>
                <code>NJX35</code>
                →
                <strong>NJX</strong>
                (Rule 1)
              </li>
              <li>
                <code>ABC123456</code>
                →
                <strong>ABC</strong>
                (Rule 1)
              </li>
              <li>
                <code>XYZ999</code>
                →
                <strong>XYZ</strong>
                (Rule 1)
              </li>
            </ul>
          </div>
          <div class="column is-half">
            <ul class="mb-0">
              <li>
                <code>NJX3H</code>
                →
                <strong>NJX3H</strong>
                (Rule 2)
              </li>
              <li>
                <code>ABC4D</code>
                →
                <strong>ABC4D</strong>
                (Rule 2)
              </li>
              <li>
                <code>12ABC</code>
                →
                <em>No prefix</em>
                (Rule 3)
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="notification is-success is-light mt-4">
        <h5 class="has-text-weight-semibold">Processing Results</h5>
        <p>After upload, you'll receive detailed feedback including:</p>
        <ul class="mb-0">
          <li>Total rows processed</li>
          <li>Successfully imported records</li>
          <li>Skipped rows with reasons (invalid revenue codes, below thresholds, duplicates)</li>
          <li>Data validation failures</li>
        </ul>
      </div>

      <div class="notification is-primary is-light mt-4">
        <p><strong>File Requirements:</strong></p>
        <ul class="mb-0">
          <li>File format: CSV only (.csv extension)</li>
          <li>Maximum file size: 100MB</li>
          <li>First row must contain headers</li>
          <li>Minimum 11 columns required</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReimbursementUploadDetail',
};
</script>

<style scoped>
.content h4 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #363636;
}

.content h5 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #363636;
}

.content ul {
  margin-top: 0.5rem;
}

.content li {
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.content li strong {
  color: #363636;
}

.table-container {
  margin: 1rem 0;
}

.tags {
  margin-top: 0.5rem;
}

.notification h5 {
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.notification ul {
  margin-bottom: 0;
}

.columns {
  margin-top: 0.5rem;
}
</style>
